import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../contexts/AuthContext'
import { handleGetRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import StatusPill from '../Verification/StatusPill'

const ShopVerificationNotification = ({ onDismiss, showSuccess = false }) => {
  const navigate = useNavigate()
  const { userType, currentUser } = useAuth()

  const { data: shopData, isLoading } = useQuery({
    queryKey: QueryKeys.myShopDetails(userType),
    queryFn: () => handleGetRequest(`${userType}s/my-shop`),
  })

  const { data: shopVerificationData, isLoading: loadingShopVerification } = useQuery({
    queryKey: QueryKeys.shopVerificationDetails(currentUser.id),
    queryFn: () => handleGetRequest(`${userType}s/my-verification`),
  })

  if (isLoading || loadingShopVerification) {
    return null
  }

  const shop = shopData?.data
  const verification = shopVerificationData?.data

  const isVerified = shop?.isApproved

  // Show success message if setup is complete and showSuccess is true
  if (isVerified && showSuccess) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 shadow-sm w-full">
        <div className="flex items-center space-x-3">
          <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-green-800">Verified!</h3>
            <p className="text-sm text-green-700">Your shop is verified!</p>
          </div>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="text-green-600 hover:text-green-800 p-1"
              aria-label="Dismiss notification"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    )
  }

  if (isVerified) {
    return null
  }

  const handleVerificationRedirect = () => {
    navigate(`/shop-verification`)
  }

  return (
    <div className="bg-green-100 text-green-800 rounded-lg p-4 border border-green-400 shadow-sm w-full">
      <div className="flex items-start space-x-3">
        <div className="flex-1">
          <h3 className="text-sm font-semibold mb-2 flex items-centre gap-2">
            You aren't verified Yet
            {verification && <StatusPill status={verification?.status} />}
          </h3>

          <p className="text-sm  mb-3">
            Get Verified to build trust and improve your impression on customers.
          </p>

          <div className="flex items-center justify-between">
            <button
              onClick={handleVerificationRedirect}
              className="bg-green-800 text-white px-4 py-2 rounded-md text-sm font-medium hover:opacity-90 transition-colors"
            >
              {verification ? 'Verification Details' : 'Get Verified'}
            </button>

            {onDismiss && (
              <button
                onClick={onDismiss}
                className="text-amber-600 hover:text-amber-800 p-1"
                aria-label="Dismiss notification"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ShopVerificationNotification
